import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { saveIcon } from "../../services/brandingService";
import { BRAND, CARD_BG, CARD_BORDER, DIVIDER, SOFT_BG, Thumb, readImageFile } from "./shared";

// Uploads as soon as a file is picked. `render` receives { open, busy } so each
// layout (service header, category row, type tile) can style its own trigger.
function IconUploader({ level, item, onSaved, render }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const data = await readImageFile(e.target.files?.[0]);
    e.target.value = "";
    if (!data) return;
    setBusy(true);
    try {
      const res = await saveIcon({ level, id: item.id, image: data });
      if (res?.success) {
        toast.success("Icon updated");
        onSaved(level, item.id, res.data?.icon);
      } else {
        toast.error(res?.message || "Could not save the icon");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} style={{ display: "none" }} />
      {render({ open: () => ref.current?.click(), busy })}
    </>
  );
}

const linkBtn = (busy) => ({
  border: "none", background: "none", padding: 0, fontSize: 12, fontWeight: 600,
  color: BRAND, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, whiteSpace: "nowrap",
});

// Small tile for a service type.
function TypeTile({ item, onSaved }) {
  return (
    <IconUploader
      level="type"
      item={item}
      onSaved={onSaved}
      render={({ open, busy }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: `1px solid ${CARD_BORDER}`, borderRadius: 10, background: SOFT_BG }}>
          <Thumb src={item.icon} name={item.name} size={30} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div title={item.name} style={{ fontSize: 13, color: "#1f2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
            <button type="button" disabled={busy} onClick={open} style={linkBtn(busy)}>
              {busy ? "Uploading…" : item.icon ? "Change" : "Upload"}
            </button>
          </div>
        </div>
      )}
    />
  );
}

function ServiceCard({ service, isOpen, onToggle, onSaved }) {
  const others = service.other_types || [];
  const hasKids = service.categories.length > 0 || others.length > 0;
  const kidsLabel = [
    service.categories.length ? `${service.categories.length} categor${service.categories.length === 1 ? "y" : "ies"}` : "",
    others.length ? `${others.length} service type${others.length === 1 ? "" : "s"}` : "",
  ].filter(Boolean).join(" · ");
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
      <IconUploader
        level="service"
        item={service}
        onSaved={onSaved}
        render={({ open, busy }) => (
          <div
            onClick={hasKids ? onToggle : undefined}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", cursor: hasKids ? "pointer" : "default", background: isOpen ? SOFT_BG : CARD_BG }}
          >
            <span style={{ width: 14, color: hasKids ? "#64748b" : "#e2e8f0", fontSize: 12 }}>{isOpen ? "▾" : "▸"}</span>
            <Thumb src={service.icon} name={service.name} size={34} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div title={service.name} style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {service.name}
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>
                {kidsLabel || "No categories"}
              </div>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); open(); }}
              style={{ border: "1px solid #F2B3C3", background: SOFT_BG, borderRadius: 8, padding: "4px 12px", fontSize: 12, fontWeight: 600, color: "#374151", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}
            >
              {busy ? "Uploading…" : service.icon ? "Change icon" : "Upload icon"}
            </button>
          </div>
        )}
      />

      {/* Fixed height: a service with many categories / types scrolls inside its card instead of stretching the page. */}
      {isOpen && hasKids && (
        <div style={{ padding: "4px 14px 14px 40px", borderTop: `1px solid ${DIVIDER}`, maxHeight: 320, overflowY: "auto" }}>
          {service.categories.map((c) => (
            <div key={c.id} style={{ marginTop: 12 }}>
              <IconUploader
                level="category"
                item={c}
                onSaved={onSaved}
                render={({ open, busy }) => (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: c.types.length ? 8 : 0 }}>
                    <Thumb src={c.icon} name={c.name} size={28} />
                    <span title={c.name} style={{ fontSize: 13, fontWeight: 600, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                    <button type="button" disabled={busy} onClick={open} style={linkBtn(busy)}>
                      {busy ? "Uploading…" : c.icon ? "Change" : "Upload"}
                    </button>
                  </div>
                )}
              />
              {c.types.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 8, paddingLeft: 38 }}>
                  {c.types.map((t) => (
                    <TypeTile key={t.id} item={t} onSaved={onSaved} />
                  ))}
                </div>
              )}
            </div>
          ))}
          {others.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {service.categories.length > 0 && (
                <div style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 8 }}>Service types without a category</div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 8 }}>
                {others.map((t) => (
                  <TypeTile key={t.id} item={t} onSaved={onSaved} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function IconsTab({ catalog, loading, onIconSaved }) {
  const [open, setOpen] = useState({});

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading your services…</div>;
  if (!catalog.length) {
    return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>You have no services yet. Add services in Admin Setup first.</div>;
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
        Upload an icon for each service, category and service type. Open a service to see its categories.
      </div>
      {catalog.map((s) => (
        <ServiceCard
          key={s.id}
          service={s}
          isOpen={!!open[s.id]}
          onToggle={() => setOpen((o) => ({ ...o, [s.id]: !o[s.id] }))}
          onSaved={onIconSaved}
        />
      ))}
    </div>
  );
}
